import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const DemoResults = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingAnalysis, setPendingAnalysis] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/property-analysis-wizard?fromDemo=true');
    }
  }, [user, navigate]);

  // Load pending analysis data
  useEffect(() => {
    const pending = localStorage.getItem('pendingAnalysis');
    if (pending) {
      try {
        setPendingAnalysis(JSON.parse(pending));
      } catch (e) {
        console.error('Error parsing pending analysis:', e);
      }
    } else {
      // No pending analysis, redirect to wizard
      navigate('/property-analysis-wizard');
    }
  }, [navigate]);

  const formatCurrency = (value) => {
    if (!value) return '0 $';
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate some mock results from the pending analysis
  const purchasePrice = parseFloat(pendingAnalysis?.purchasePrice) || 500000;
  const monthlyRevenue = parseFloat(pendingAnalysis?.totalMonthlyRevenue) || 3500;
  const monthlyExpenses = parseFloat(pendingAnalysis?.totalMonthlyExpenses) || 1500;
  const monthlyCashflow = monthlyRevenue - monthlyExpenses;
  const annualCashflow = monthlyCashflow * 12;
  const capRate = ((annualCashflow / purchasePrice) * 100).toFixed(2);
  const propertyAddress = pendingAnalysis?.address || 'Propriété analysée';

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      <NavigationHeader demoMode={true} />

      <div style={{ position: 'relative', minHeight: 'calc(100vh - 64px)' }}>
        {/* Blurred background content - simulated results */}
        <div style={{
          filter: 'blur(8px)',
          pointerEvents: 'none',
          userSelect: 'none',
          padding: 'var(--spacing-xl)',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {/* Property Header */}
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-xl)',
            marginBottom: 'var(--spacing-lg)',
            border: '1px solid var(--color-border)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="Building2" size={32} color="white" />
              </div>
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>{propertyAddress}</h1>
                <p style={{ color: 'var(--color-muted-foreground)', margin: 0 }}>Analyse complète</p>
              </div>
            </div>

            {/* Score */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-xl)',
              padding: 'var(--spacing-xl)',
              background: 'var(--color-success)',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', fontWeight: 700, color: 'white' }}>78</div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>Score global</div>
              </div>
              <div style={{ color: 'white', fontSize: '1.25rem' }}>Bon investissement</div>
            </div>
          </div>

          {/* KPIs Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <div style={{
              background: 'var(--color-card)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              border: '1px solid var(--color-border)',
              textAlign: 'center'
            }}>
              <Icon name="TrendingUp" size={24} color="var(--color-success)" style={{ marginBottom: 'var(--spacing-sm)' }} />
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{formatCurrency(monthlyCashflow)}</div>
              <div style={{ color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>Cashflow mensuel</div>
            </div>
            <div style={{
              background: 'var(--color-card)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              border: '1px solid var(--color-border)',
              textAlign: 'center'
            }}>
              <Icon name="Percent" size={24} color="var(--color-primary)" style={{ marginBottom: 'var(--spacing-sm)' }} />
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{capRate}%</div>
              <div style={{ color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>Taux de capitalisation</div>
            </div>
            <div style={{
              background: 'var(--color-card)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              border: '1px solid var(--color-border)',
              textAlign: 'center'
            }}>
              <Icon name="PiggyBank" size={24} color="var(--color-warning)" style={{ marginBottom: 'var(--spacing-sm)' }} />
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>12.4%</div>
              <div style={{ color: 'var(--color-muted-foreground)', fontSize: '0.875rem' }}>ROI sur 10 ans</div>
            </div>
          </div>

          {/* Projection Chart Placeholder */}
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-xl)',
            border: '1px solid var(--color-border)',
            marginBottom: 'var(--spacing-lg)'
          }}>
            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Projection sur 10 ans</h3>
            <div style={{
              height: '200px',
              background: 'linear-gradient(to right, var(--color-primary), var(--color-success))',
              borderRadius: 'var(--radius-md)',
              opacity: 0.2
            }} />
          </div>

          {/* AI Analysis Placeholder */}
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-xl)',
            border: '1px solid var(--color-border)'
          }}>
            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>Analyse IA</h3>
            <div style={{ color: 'var(--color-muted-foreground)', lineHeight: 1.8 }}>
              <p>Cette propriété présente un excellent potentiel d'investissement avec un cashflow positif dès le premier mois...</p>
              <p>Les points forts incluent un emplacement stratégique et un rendement supérieur à la moyenne du marché...</p>
            </div>
          </div>
        </div>

        {/* Overlay with CTA */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            background: 'var(--color-card)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--spacing-2xl)',
            boxShadow: 'var(--shadow-xl)',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
            border: '1px solid var(--color-border)'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--spacing-lg)'
            }}>
              <Icon name="Lock" size={36} color="white" />
            </div>

            <h2 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: 'var(--color-foreground)',
              margin: '0 0 var(--spacing-sm)'
            }}>
              Créez un compte pour voir vos résultats
            </h2>

            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.9375rem',
              color: 'var(--color-muted-foreground)',
              margin: '0 0 var(--spacing-lg)',
              lineHeight: 1.6
            }}>
              Votre analyse est prête. Créez un compte gratuit pour accéder à tous les détails et sauvegarder votre analyse.
            </p>

            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="UserPlus"
              iconPosition="right"
              onClick={() => navigate('/demo-signup')}
              style={{ marginBottom: 'var(--spacing-sm)' }}
            >
              Créer mon compte gratuit
            </Button>

            <button
              onClick={() => navigate('/demo-signup')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-primary)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                cursor: 'pointer',
                padding: 'var(--spacing-sm)'
              }}
            >
              J'ai déjà un compte - Se connecter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoResults;
